import React, { useEffect, useState } from 'react';
import {
    Dropdown,
    Button,
    Form,
    Grid,
    Segment
} from 'semantic-ui-react';

import ClientRepository from '../../../common/repository/ClientRepository';
import Result from '../../../common/repository/Result';
import { toast } from '../../common/Toast';
import LocationInput, { LocationInputValue } from '../../common/LocationInput';
import StateDistrictRepository from '../../../common/repository/StateDistrictRepository';
import Page from '../Page';
import Client from '../../../models/Client';
import HospitalRepository from '../../../common/repository/HospitalRepository';

const DailyStatusDownload: React.FC = () => {
    interface DropdownOption {
        key: string;
        text: string;
        value: string;
    }

    const [loading, setLoading] = useState(false);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [clients, setClients] = useState<Client[]>([]);
    const [clientOptions, setClientOptions] = useState<DropdownOption[]>([]);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);

    const [caseTypeOptions, setCaseTypeOptions] = useState<DropdownOption[]>([]);
    const [selectedCaseType, setSelectedCaseType] = useState<string>("");

    const [insuredLocation, setInsuredLocation] = useState<LocationInputValue | null>(null);

    const [hospitalLocation, setHospitalLocation] = useState<LocationInputValue | null>(null);
    const [hospitalOptions, setHospitalOptions] = useState<DropdownOption[]>([]);
    const [selectedHospital, setSelectedHospital] = useState<string>("");


    const clientRepo = new ClientRepository();
    const stateDistrictRepo = new StateDistrictRepository();
    const hospitalRepo = new HospitalRepository();

    const handleDailyStatusDownload = async () => {
        try {
            const payload = {
                from: fromDate ?? undefined,
                to: toDate ?? undefined,
                clientId: selectedClient?.getId() ?? undefined,
                clientType: selectedClient?.getType(),
                caseTypeId: selectedCaseType ?? undefined,
                state: insuredLocation?.state ?? undefined,
                district: insuredLocation?.district ?? undefined,
                hospitalId: selectedHospital ?? undefined,
            };

            console.log(payload);

            // const result = await reportRepo.downloadDailyStatus(payload);

            // if (result instanceof Result.Success) {
            //     toast.success("Report downloaded successfully");
            // } else {
            //     toast.error(result.message);
            // }

        } catch (error) {
            toast.error((error as Error).message || "Something went wrong");
        }
    };

    useEffect(() => {
        const fetchClient = async () => {
            try {
                setLoading(true);
                const result = await clientRepo.getClients({});

                if (result instanceof Result.Success) {
                    const clientList = result.data.items;

                    setClients(clientList);

                    const options: DropdownOption[] = clientList.map((client) => ({
                        key: client.getId(),
                        text: client.getName(),
                        value: client.getId(),
                    }));

                    setClientOptions(options);
                }
            } catch (e) {
                toast.error((e as Error).message || "Something went wrong");
            }
            finally {
                setLoading(false);
            }
        };
        fetchClient();
    }, []);

    useEffect(() => {
        const fetchHospitals = async () => {
            if (
                !hospitalLocation?.state ||
                !hospitalLocation?.district
            ) {
                setHospitalOptions([]);
                return;
            }

            try {
                setLoading(true);
                const result = await hospitalRepo.getHospitals({
                    location: {
                        state: hospitalLocation.state,
                        district: hospitalLocation.district,
                    },
                });

                if (result instanceof Result.Success) {
                    const options: DropdownOption[] = result.data.items.map(
                        (hospital) => ({
                            key: hospital.getId(),
                            text: hospital.getName(),
                            value: hospital.getId(),
                        })
                    );

                    setHospitalOptions(options);
                } else {
                    setHospitalOptions([]);
                }
            } catch (e) {
                setHospitalOptions([]);
                toast.error((e as Error).message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        };

        fetchHospitals();
    }, [hospitalLocation]);


    const isDownloadDisabled = () => {
        if(loading) return true;
        if (!fromDate || !toDate) {
            return true;
        }
        return false;
    };

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");

        // Client
        setSelectedClient(null);
        setCaseTypeOptions([]);
        setSelectedCaseType("");

        // Locations
        setInsuredLocation(null);
        setHospitalLocation(null);

        // Hospital
        setHospitalOptions([]);
        setSelectedHospital("");
    };


    return (
        <Page
            title="Daily Status Sheet"
            description="Download Daily Status Report using the filters below."
        >
            <Segment padded>
                <Form>
                    <Grid stackable columns={4}>
                        <Grid.Column>
                            <Form.Input
                                fluid
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Input
                                fluid
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Insurance Company</label>

                                <Dropdown
                                    fluid
                                    search
                                    selection
                                    clearable
                                    placeholder="Select Insurance Company"
                                    options={clientOptions}
                                    value={selectedClient?.getId() || ""}
                                    onChange={(e, data) => {
                                        const client = clients.find(
                                            (c) => c.getId() === String(data.value)
                                        );

                                        setSelectedClient(client || null);
                                        setSelectedCaseType("");

                                        if (client) {
                                            setCaseTypeOptions(
                                                client.getCaseTypes().map((caseType) => ({
                                                    key: caseType.getTitle(),
                                                    text: caseType.getTitle(),
                                                    value: caseType.getId(),
                                                }))
                                            );
                                        } else {
                                            setCaseTypeOptions([]);
                                        }
                                    }}
                                />
                            </Form.Field>
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Case Type</label>

                                <Dropdown
                                    fluid
                                    search
                                    selection
                                    clearable
                                    disabled={!selectedClient}
                                    placeholder="All Case Types"
                                    options={caseTypeOptions}
                                    value={selectedCaseType}
                                    onChange={(e, data) =>
                                        setSelectedCaseType(String(data.value))
                                    }
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    <Grid stackable columns={3} style={{ marginTop: "0.5rem" }}>
                        <Grid.Column>
                            <Form.Field>
                                <label>Insured Location</label>
                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <LocationInput
                                            value={insuredLocation}
                                            onChange={setInsuredLocation}
                                            stateDistrictRepo={stateDistrictRepo}
                                        />
                                    </div>
                                </div>
                            </Form.Field>
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Hospital Location</label>

                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <LocationInput
                                            value={hospitalLocation}
                                            onChange={setHospitalLocation}
                                            stateDistrictRepo={stateDistrictRepo}
                                        />
                                    </div>
                                </div>
                            </Form.Field>
                        </Grid.Column>

                        <Grid.Column>
                            <Form.Field>
                                <label>Hospital</label>

                                <Dropdown
                                    fluid
                                    search
                                    selection
                                    clearable
                                    disabled={
                                        !hospitalLocation?.state ||
                                        !hospitalLocation?.district
                                    }
                                    placeholder="Select Hospital"
                                    options={hospitalOptions}
                                    value={selectedHospital}
                                    onChange={(e, data) =>
                                        setSelectedHospital(String(data.value))
                                    }
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>

                    <Grid stackable verticalAlign="bottom" style={{ marginTop: "0.5rem" }}>
                        <Grid.Column
                            textAlign="right"
                            verticalAlign="bottom"
                        >
                            <Button
                                basic
                                icon="undo"
                                content="Clear Filters"
                                onClick={handleClearFilters}
                                style={{ marginRight: "0.75rem" }}
                            />
                            <Button
                                primary
                                icon="download"
                                content="Download Report"
                                disabled={isDownloadDisabled()}
                                onClick={handleDailyStatusDownload}
                            />
                        </Grid.Column>
                    </Grid>
                    
                </Form>
            </Segment>
        </Page>
    );

}

export default DailyStatusDownload;
